package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.dashboard.AppDialogs
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.ScreenManager
import org.example.project.enginechat.ChatMessageList
import org.example.project.enginechat.userDisplayName
import org.example.project.service.LoginService
import org.example.project.service.RollenService
import org.example.project.settings.*
import org.example.project.utils.All
import org.example.project.utils.UserAvatarCircle
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.disabled

/** Read-only LinkedIn conversations; opening a chat never creates or changes a prospect. */
@Composable
fun LinkedInChat(node: String, onOpenFlow: ((String) -> Unit)? = null) {
    if (node != "SYS" || !ScreenManager.isLinkedInNode()) return
    val scope = rememberCoroutineScope()
    val sharedKpis = LocalLinkedInKpis.current
    var contacts by remember { mutableStateOf<List<All>>(emptyList()) }
    var checkpointContacts by remember { mutableStateOf<List<All>>(emptyList()) }
    var checkpointDeadlines by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }
    var selectedId by remember { mutableStateOf<String?>(null) }
    var messages by remember { mutableStateOf<List<All>>(emptyList()) }
    var query by remember { mutableStateOf("") }
    var chatFilter by remember { mutableStateOf("Alle") }
    var refreshing by remember { mutableStateOf(false) }
    val selected = contacts.firstOrNull { it.id == selectedId }
    val canOpenFlow = RollenService.hasReadPermission("LinkedInFlow")
    fun openFlow(contactId: String) {
        if (!canOpenFlow || contacts.firstOrNull { it.id == contactId }?.let { it.linkedinGeschlossen || it.linkedinSnapshotGeloescht } == true) return
        if (onOpenFlow != null) onOpenFlow(contactId)
        else {
            val encoded = js("encodeURIComponent")(contactId) as String
            window.location.hash = window.location.hash.substringBefore('?') + "?screen=SYSVertriebsDashboard&linkedinContact=$encoded"
        }
    }

    suspend fun loadContacts(initialOnly: Boolean = false) {
        val id = selectedId
        val data = if (sharedKpis != null) {
            sharedKpis.refresh(initialOnly = initialOnly)
            All(linkedinKontakte = sharedKpis.contacts, vertriebCheckpointFristen = sharedKpis.deadlines)
        } else LinkedInFlowApi.chat(id)
        checkpointContacts = data.linkedinKontakte.filterNot { it.linkedinSnapshotGeloescht }
        checkpointDeadlines = data.vertriebCheckpointFristen
        contacts = data.linkedinKontakte.filter { it.linkedinHatNachrichten }
            .sortedWith { a, b ->
            val newestIncoming = linkedInLastIncomingTime(b).compareTo(linkedInLastIncomingTime(a))
            if (newestIncoming != 0) return@sortedWith newestIncoming
            val last = a.nachname.asDynamic().localeCompare(b.nachname, "de", js("({sensitivity:'base'})")) as Int
            if (last != 0) last else {
                val first = a.vorname.asDynamic().localeCompare(b.vorname, "de", js("({sensitivity:'base'})")) as Int
                if (first != 0) first else a.firma.asDynamic().localeCompare(b.firma, "de", js("({sensitivity:'base'})")) as Int
            }
        }
        if (selectedId != null && contacts.none { it.id == selectedId }) { selectedId = null; messages = emptyList() }
        else if (sharedKpis == null && selectedId == id) messages = data.linkedinNachrichten
    }
    fun refresh(initialOnly: Boolean = false) {
        if (refreshing) return
        refreshing = true
        scope.launch {
            try {
                loadContacts(initialOnly)
            } catch (e: CancellationException) { throw e }
            catch (e: Exception) { AppDialogs.alert(e.message ?: "LinkedIn-Chat konnte nicht aktualisiert werden.") }
            finally { refreshing = false }
        }
    }
    LaunchedEffect(LinkedInSyncState.version) { refresh(initialOnly = true) }
    LaunchedEffect(sharedKpis?.contacts) {
        if (sharedKpis?.loaded == true) loadContacts(initialOnly = true)
    }
    LaunchedEffect(selectedId) {
        messages = emptyList()
        val id = selectedId ?: return@LaunchedEffect
        refreshing = true
        try { messages = LinkedInFlowApi.messages(id) }
        catch (e: CancellationException) { throw e }
        catch (e: Exception) { AppDialogs.alert(e.message ?: "Nachrichten konnten nicht geladen werden.") }
        finally { refreshing = false }
    }

    Div({ style { height(100.percent); minHeight(0.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
    if (sharedKpis == null) {
        LinkedInCheckpointTiles(checkpointContacts, "", !refreshing && canOpenFlow, checkpointDeadlines, ::openLinkedInCheckpoint)
        Div({ style { height(1.px); flexShrink(0); backgroundColor(Color("rgba(255,255,255,.14)")) } })
    }
    Div({ style {
        display(DisplayStyle.Grid); gridTemplateColumns("minmax(max-content, 1fr) 560px minmax(0, 1fr)")
        flex("1"); minHeight(0.px); overflowX("auto"); overflowY("hidden"); color(UISettings.STATE_BUTTON_TEXT)
    } }) {
        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); height(100.percent); property("width", "max-content"); minHeight(0.px) } }) {
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); padding(12.px); flexShrink(0) } }) {
                Div({ style { display(DisplayStyle.Flex); flex("1"); minWidth(0.px) } }) { EditorInput(query, "Kontakte suchen", { query = it }, grow = true) }
                LumaticaButton("Aktualisieren", { refresh() }, variant = LumaticaButtonVariant.State, enabled = !refreshing,
                    content = { Img(src = "images/refresh.svg", attrs = { attr("alt", "Aktualisieren"); attr("title", "Aktualisieren"); style { width(18.px); height(18.px); property("filter", "brightness(0) invert(1)") } }) })
            }
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px); padding(0.px, 12.px, 10.px); flexShrink(0); fontSize(13.px); color(DashboardTheme.TEXT_MUTED) } }) {
                Button(attrs = {
                    attr("aria-label", "Markierten Kontakt im DM Flow öffnen"); attr("title", "Markierten Kontakt im DM Flow öffnen")
                    if (selected == null || !canOpenFlow || selected.linkedinGeschlossen || selected.linkedinSnapshotGeloescht) disabled()
                    onClick { selected?.let { openFlow(it.id) } }
                    style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); width(24.px); height(24.px); padding(2.px); flexShrink(0); backgroundColor(Color.transparent); border(0.px); cursor(if (selected != null && canOpenFlow && !selected.linkedinGeschlossen && !selected.linkedinSnapshotGeloescht) "pointer" else "default"); if (selected == null || !canOpenFlow || selected.linkedinGeschlossen || selected.linkedinSnapshotGeloescht) opacity(.45) }
                }) { Img(src = "images/stift.svg", attrs = { attr("alt", ""); style { width(16.px); height(16.px); property("filter", "brightness(0) invert(1)") } }) }
                Div({ style { width(36.px); flexShrink(0) } }) {
                    AppDropdown(options = listOf("Alle", "Nur gesendet", "Nur beantwortet").map { All(key = it, label = it) },
                        selectedValue = chatFilter, onChange = { chatFilter = it }, triggerIcon = "filter",
                        appearance = AppDropdownAppearance.Field, menuMode = AppDropdownMenuMode.Overlay,
                        attrs = { attr("aria-label", "Chats filtern: $chatFilter"); attr("title", "Chats filtern: $chatFilter"); style { padding(0.px) } })
                }
                Text("${contacts.size} Chats geladen")
            }
            Div({ style { flex("1"); minHeight(0.px); overflowY("auto"); property("scrollbar-gutter", "stable") } }) {
                contacts.filter { contact -> when (chatFilter) {
                    "Nur gesendet" -> contact.linkedinHatGesendeteNachrichten && contact.linkedinLetzteAntwortAm.isBlank()
                    "Nur beantwortet" -> contact.linkedinLetzteAntwortAm.isNotBlank()
                    else -> true
                } }.filter { contact -> query.isBlank() || listOf(contact.vorname, contact.nachname, contact.firma, contact.ansprechpartnerPosition).joinToString(" ").contains(query, ignoreCase = true) }.forEach { contact ->
                    key(contact.id) { LinkedInChatContactRow(contact, selectedId == contact.id,
                        onSelect = { if (!refreshing) selectedId = contact.id }) }
                }
            }
        }
        Div({ style { height(100.percent); width(560.px); minHeight(0.px); overflow("hidden") } }) {
            selected?.let { contact ->
                key(contact.id) { ChatMessageList(messages, { it.linkedinNachrichtEigen }, { "" }, scrollable = true) }
            }
        }
        Div {}
    }
    }
}

private fun linkedInLastIncomingTime(contact: All): Double =
    kotlin.js.Date(contact.linkedinLetzteAntwortAm).getTime().let { if (it.isNaN()) 0.0 else it }

@Composable
internal fun LinkedInChatContactRow(contact: All, selected: Boolean, onSelect: () -> Unit) {
    Div({
        attr("role", "button"); attr("aria-pressed", selected.toString()); tabIndex(0)
        attr("title", listOf(userDisplayName(contact), contact.firma, contact.ansprechpartnerPosition).filter { it.isNotBlank() }.joinToString(" | "))
        onClick { onSelect() }
        onKeyDown { if (it.key == "Enter" || it.key == " ") { it.preventDefault(); onSelect() } }
        style { padding(10.px, 12.px, 10.px, 14.px); cursor("pointer"); display(DisplayStyle.Grid); gridTemplateColumns("64px max-content"); alignItems(AlignItems.Center); gap(10.px); if (selected) backgroundColor(Color("rgba(255,255,255,.10)")) }
    }) {
        UserAvatarCircle(user = contact, fallbackLabel = userDisplayName(contact), sizePx = 60, background = UISettings.SALES_KPI_BG)
        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); minWidth(0.px); gap(3.px) } }) {
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
                Span({ style { fontSize(16.px); fontWeight("750"); color(DashboardTheme.TEXT_LIGHT); whiteSpace("nowrap") } }) { Text(userDisplayName(contact)) }
            }
            Span({ style { fontSize(13.px); color(DashboardTheme.TEXT_MUTED); whiteSpace("nowrap") } }) { Text(contact.firma) }
        }
    }
}
